"use client"

import React, { useCallback, useEffect, useState } from "react"
import clsx from "clsx"
import { FormattedMessage } from "react-intl"
import {
  Channel,
  ChannelList,
  MessageInput,
  Thread,
  VirtualizedMessageList,
} from "stream-chat-react"

import {
  Box,
  Button,
  Loading,
  MESSENGER_URI,
  Redirect,
  useBreakpoint,
  useChannel,
  useMainInboxChannelIds,
  useMessageChannelByStreamId,
  useMessageRequestChannelIds,
  useMessagingContext,
  useUnreadCount,
  useUserBlockedMe,
  useUserContext,
  useUserMessageChannel,
} from "@bullseye/components"
import { usePathname, useRouter, useSearchParams } from "@bullseye/navigation"

import {
  MessageChannelHeader,
  MessengerChannelPreview,
  MessengerInput,
  MessengerUserSuspended,
  NoChannelSelected,
  StreamChatAvatar,
} from "../../components"

type Channel = {
  id: string
  data?: {
    hidden?: boolean
  }
}

const A11yScroller = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => <div {...props} ref={ref} role="group" />)
A11yScroller.displayName = "A11yScroller"

const MessengerPage = () => {
  const { user } = useUserContext()
  const {
    currentChannelId,
    setCurrentChannelId,
    enableChannelLists,
    isLoadingChannelLists,
  } = useMessagingContext()

  const isSm = useBreakpoint("md", "down")

  const sp = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const channelId = sp?.get("channel") ?? undefined
  const { channel: data, isLoading: channelLoading } = useChannel(channelId)
  const channel = data as Channel
  const { messageChannel } = useMessageChannelByStreamId(channel?.id)
  // Fetch these at page level to prevent cancelled requests when Channel component remounts
  const { userMessageChannel } = useUserMessageChannel(messageChannel?.id)
  const { data: isBlockedByUser, isLoading: isBlockedLoading } =
    useUserBlockedMe(messageChannel?.dm_user?.id)
  const unreadCount = useUnreadCount()
  const mainInboxChannelIds = useMainInboxChannelIds()
  const messageRequestsChannelIds = useMessageRequestChannelIds()
  const [activeTab, setActiveTab] = useState<"all" | "requests">("all")

  // Enable lazy loading of channel lists when messenger page mounts
  useEffect(() => {
    enableChannelLists()
  }, [enableChannelLists])

  // Sync the URL channel ID with the messaging context to prevent unnecessary re-renders
  useEffect(() => {
    if (channelId && currentChannelId !== channelId) {
      setCurrentChannelId(channelId)
    }
  }, [channelId, currentChannelId, setCurrentChannelId])

  const userIsSuspended = messageChannel?.dm_user?.is_suspended || false
  const channelIds =
    activeTab === "all" ? mainInboxChannelIds : messageRequestsChannelIds

  const renderPreview = useCallback(
    ({ channel = null, lastMessage = null, unread = null }) => {
      const id = (channel as Channel)?.id
      if (!channelIds.includes(id) && id !== channelId) {
        return null
      }

      return (
        <MessengerChannelPreview
          channelId={id}
          unread={unread}
          lastMessage={lastMessage}
        />
      )
    },
    [channelIds, channelId],
  )

  if (channel?.data?.hidden) {
    return <Redirect href={MESSENGER_URI} push />
  }

  const handleAcceptMessage = () => {
    setActiveTab("all")
    router.replace(`${pathname}?channel=${channelId}`)
  }

  return (
    <Box className="flex h-full w-full flex-col overflow-hidden">
      <Box className="hidden md:flex">
        <MessageChannelHeader messageChannel={messageChannel} />
      </Box>
      <Box className="flex h-full w-full flex-1 flex-row">
        <Box
          className={clsx("flex w-full overflow-hidden md:w-[30%]", {
            hidden: isSm && channelId,
          })}
        >
          <Box className="flex md:hidden">
            <MessageChannelHeader messageChannel={messageChannel} />
          </Box>
          <Box className="flex flex-row items-center justify-start gap-2 border-b border-r border-solid border-gray-100 p-3 px-4 pt-1">
            <Button
              compact
              as="button"
              handlePress={() => {
                setActiveTab("all")
                router.replace(pathname)
              }}
              variant="secondary"
              borderless={activeTab === "requests"}
            >
              <FormattedMessage id="messenger.tabs.all" />{" "}
              {unreadCount > 0 && `(${unreadCount})`}
            </Button>
            <Button
              compact
              as="button"
              handlePress={() => {
                setActiveTab("requests")
                router.replace(pathname)
              }}
              variant="secondary"
              borderless={activeTab === "all"}
            >
              <FormattedMessage id="messenger.tabs.requests" />
            </Button>
          </Box>
          {isLoadingChannelLists ? (
            <Box className="flex h-full w-full items-center justify-center p-8">
              <Loading />
            </Box>
          ) : (
            <ChannelList
              filters={{
                members: {
                  $in: [user.stream_id],
                },
                id: {
                  // Include the URL's channelId to ensure deep-linked channels are always shown
                  $in:
                    channelIds.length > 0 || channelId
                      ? [...new Set([...channelIds, channelId].filter(Boolean))]
                      : ["fake-id-does-not-exist"],
                },
                // This caused an OOM for the linter, so we disable the rule for this line.
                /* eslint-disable sort-keys */
                $or: [
                  {
                    last_message_at: { $gte: "2010-04-01T00:00:00.00Z" },
                  },
                  {
                    created_by_id: { $eq: user.stream_id },
                  },
                ],
                /* eslint-enable sort-keys */
              }}
              sort={{
                last_message_at: -1,
              }}
              Preview={renderPreview}
              customActiveChannel={channelId}
              setActiveChannelOnMount={false}
            />
          )}
        </Box>

        <NoChannelSelected hidden={!!channelId} />

        {!(isSm || !channelId) && (
          <Box className={clsx("w-full overflow-hidden md:flex md:w-[70%]")}>
            <Channel channel={channel as any} Avatar={StreamChatAvatar}>
              <Box className="dvh-full h-full w-full md:h-full">
                <Box className="flex md:hidden">
                  <MessageChannelHeader messageChannel={messageChannel} />
                </Box>
                <MessengerUserSuspended hidden={!userIsSuspended} />
                <Box
                  className={clsx("flex-1 pt-4", {
                    hidden: userIsSuspended,
                  })}
                >
                  {channelLoading ? (
                    <Box className="flex h-full w-full items-center justify-center">
                      <Loading />
                    </Box>
                  ) : (
                    <VirtualizedMessageList
                      scrollToLatestMessageOnFocus
                      additionalVirtuosoProps={{
                        components: { Scroller: A11yScroller as any },
                      }}
                    />
                  )}
                </Box>
                <MessengerInput
                  messageChannel={messageChannel}
                  userMessageChannel={userMessageChannel}
                  isBlockedByUser={isBlockedByUser}
                  isBlockedLoading={isBlockedLoading}
                  handleAcceptMessage={handleAcceptMessage}
                  InputComponent={MessageInput}
                />
              </Box>
              <Thread />
            </Channel>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default MessengerPage
